import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { UserRole, isAdminUser, CommunicationLanguage } from '../types/auth';
import { adminService } from '../services/admin';
import {
  InstitutionDto,
  InstitutionMembershipCategory,
  AdministrativeStatus,
  InstitutionContactPreference,
  getInstitutionDisplayName,
  normalizeCommunicationLanguage,
} from '../types/institution';
import type { BilingualAddress, BilingualName, BilingualText } from '../types/common';
import {
  ChangeRequest,
  ChangeRequestStatus,
  EntityType,
} from '../types/changeRequest';
import { Document, DocumentType } from '../types/document';
import {
  InternationalCommittee,
} from '../types/committee';
import { Title } from '../types/member';
import { institutionService } from '../services/institutions';
import { changeRequestService } from '../services/changeRequests';
import { documentService } from '../services/documents';
import { committeeService } from '../services/committees';
import FormField from '../components/common/FormField';
import LoadingSpinner from '../components/common/LoadingSpinner';
import FileUpload from '../components/common/FileUpload';
import ConfirmDialog from '../components/common/ConfirmDialog';
import FormAlert from '../components/registration/FormAlert';
import PhoneWithDialCode from '../components/registration/PhoneWithDialCode';
import {
  ICOM_INSTITUTION_TYPE_KEYS,
  formatInstitutionTypeLabel,
} from '../data/icomInstitutionTypes';
import { Container, Stack, Grid, Flex } from '../design-system/components/Layout';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { StatusIndicator } from '../design-system/patterns/StatusIndicator';
import { PageHeader } from '../design-system/patterns/PageHeader';
import { Tabs, TabPanel } from '../design-system/patterns/Tabs';
import { FormSection } from '../design-system/patterns/FormSection';
import Avatar from '../components/common/Avatar';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { animations } from '../design-system/tokens/animations';
import { shadows } from '../design-system/tokens/shadows';
import { hexToRgba } from '../utils/colorUtils';
import {
  calculateInstitutionProfileCompletion,
  getInstitutionFieldLabel,
  getMembershipCategoryLabel,
} from '../utils/institutionDashboardViewModel';
import { headerWatermarkStyles } from '../components/dashboard/dashboardWatermark';
import { MonumentCaption } from '../components/dashboard/MonumentCaption';
import { useMonumentWatermark } from '../hooks/useMonumentWatermark';
import * as validation from '../utils/validation';
import { getApiErrorMessage, getValidationErrorMessage } from '../utils/apiErrors';
import { tryNormalizeEnumValue } from '../utils/enumNormalize';
import { uniqueInternationalCommittees } from '../utils/committeeUtils';
import { breakpoints } from '../design-system/tokens/breakpoints';

/* ─── Layout ─────────────────────────────────────────────────────────────── */

const InstitutionProfileContainer = styled(Container)`
  max-width: 1200px;
`;

const FormGrid = styled(Grid)`
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing[6]};
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonGroup = styled(Stack)`
  margin-top: ${spacing[8]};
  padding-top: ${spacing[8]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};
`;

const HeroCard = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 180px;
  display: flex;
  align-items: center;
  padding: ${spacing[8]};
  margin-bottom: ${spacing[2]};
  border-radius: ${borders.radius.lg};
  background: transparent;
  ${headerWatermarkStyles}

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[5]} ${spacing[4]};
    min-height: auto;
  }
`;

const HeroAccentBar = styled.span`
  display: block;
  width: 58px;
  height: 3px;
  border-radius: 2px;
  background: ${props => props.theme.colors.accent};
  margin-bottom: ${spacing[4]};
`;

const HeroRow = styled(Flex)`
  position: relative;
  z-index: 2;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    > button {
      width: 100%;
    }
  }
`;

const TabContentCard = styled(Card)`
  padding: ${spacing[8]};
`;

const OverviewCard = styled(Card)`
  padding: ${spacing[8]};
`;

const KeyInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${spacing[4]};
  margin-top: ${spacing[4]};
`;

const KeyInfoItem = styled.div`
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.surfaceHover};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[4]} ${spacing[5]};
`;

const InfoItem = styled.div<{ $span?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  min-width: 0;
  ${props => props.$span && `grid-column: 1 / -1;`}
`;

const InfoLabel = styled(Text)`
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  font-size: ${typography.fontSize.xs};
`;

const EmptyHint = styled(Text)`
  font-style: italic;
  color: ${props => props.theme.colors.textMuted};
`;

const BilingualStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BilingualLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing[2]};
  min-width: 0;
`;

const LangTag = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  opacity: 0.85;
  line-height: 1.4;
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  padding-bottom: ${spacing[6]};
  margin-bottom: ${spacing[6]};
  border-bottom: ${borders.width.thin} solid
    ${props => props.theme.colors.borderLight || props.theme.colors.border};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.primary};
  line-height: 1.3;
`;

const SectionHint = styled(Text)`
  display: block;
  margin-top: -${spacing[2]};
`;

const PendingBadge = styled.div`
  padding: ${spacing[2]} ${spacing[4]};
  background: ${props => props.theme.colors.warning}20;
  border: 1px solid ${props => props.theme.colors.warning};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.text};
`;

const ApprovalBanner = styled.div`
  padding: ${spacing[3]} ${spacing[4]};
  background: ${props => hexToRgba(props.theme.colors.primary, 0.08)};
  border: ${borders.width.thin} solid ${props => hexToRgba(props.theme.colors.primary, 0.25)};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing[4]};
`;

const CompletenessShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  padding: ${spacing[4]} ${spacing[5]};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.surface} 0%,
    ${props => props.theme.colors.icomLightBlue || hexToRgba(props.theme.colors.primary, 0.06)} 100%
  );
`;

const CompletenessBarTrack = styled.div`
  height: 8px;
  border-radius: ${borders.radius.full};
  background: ${props => props.theme.colors.surfaceHover};
  overflow: hidden;
`;

const CompletenessBarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${props => props.$pct}%;
  background: ${props =>
    props.$pct >= 100
      ? props.theme.colors.success
      : props.$pct >= 50
        ? props.theme.colors.icomGold || props.theme.colors.accent
        : props.theme.colors.warning};
  transition: width ${animations.duration.normal} ${animations.easing.easeOut};
`;

const MissingList = styled.ul`
  margin: 0;
  padding-left: ${spacing[5]};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;

const CommitteeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const CommitteeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing[3]} ${spacing[4]};
  background: ${props => props.theme.colors.surfaceHover};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  gap: ${spacing[3]};
`;

const AddCommitteeSection = styled.div`
  margin-top: ${spacing[4]};
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.surfaceHover};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.base} dashed ${props => props.theme.colors.border};
`;

const ChangeRequestCard = styled.div`
  background: ${props => props.theme.colors.surfaceHover};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  padding: ${spacing[4]};
  margin-bottom: ${spacing[3]};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }
`;

const ChangeRequestRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing[2]};
  gap: ${spacing[3]};
  &:last-child {
    margin-bottom: 0;
  }
`;

const DocumentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing[3]};
  background: ${props => props.theme.colors.surfaceHover};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  margin-bottom: ${spacing[2]};
  gap: ${spacing[3]};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }
`;

const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocumentName = styled.div`
  color: ${props => props.theme.colors.text};
  font-weight: ${typography.fontWeight.medium};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  margin-bottom: ${spacing[1]};
`;

const DocumentMeta = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
`;

const DocumentActions = styled.div`
  display: flex;
  gap: ${spacing[2]};
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  padding: ${spacing[8]} ${spacing[5]};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-style: italic;
`;

/* ─── Display helpers ────────────────────────────────────────────────────── */

function hasText(value?: string | null): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function preferGreek(lang: string): boolean {
  return lang.startsWith('el') || lang === 'gr';
}

const BilingualTextValue: React.FC<{
  en?: string | null;
  gr?: string | null;
  preferGr: boolean;
  emptyLabel: string;
}> = ({ en, gr, preferGr, emptyLabel }) => {
  const enVal = hasText(en) ? en.trim() : null;
  const grVal = hasText(gr) ? gr.trim() : null;

  if (!enVal && !grVal) {
    return <EmptyHint size="sm">{emptyLabel}</EmptyHint>;
  }

  if (enVal && grVal && enVal === grVal) {
    return (
      <Text size="base" weight="medium" color="text">
        {enVal}
      </Text>
    );
  }

  if (enVal && grVal) {
    const primary = preferGr ? grVal : enVal;
    const secondary = preferGr ? enVal : grVal;
    const primaryLang = preferGr ? 'EL' : 'EN';
    const secondaryLang = preferGr ? 'EN' : 'EL';
    return (
      <BilingualStack>
        <BilingualLine>
          <LangTag aria-hidden>{primaryLang}</LangTag>
          <Text size="base" weight="medium" color="text">
            {primary}
          </Text>
        </BilingualLine>
        <BilingualLine>
          <LangTag aria-hidden>{secondaryLang}</LangTag>
          <Text size="sm" color="secondary">
            {secondary}
          </Text>
        </BilingualLine>
      </BilingualStack>
    );
  }

  return (
    <Text size="base" weight="medium" color="text">
      {enVal || grVal}
    </Text>
  );
};

const BilingualNameValue: React.FC<{
  name?: BilingualName | null;
  preferGr: boolean;
  emptyLabel: string;
}> = ({ name, preferGr, emptyLabel }) => {
  if (!name) return <EmptyHint size="sm">{emptyLabel}</EmptyHint>;
  const en = [name.firstNameEn, name.lastNameEn].filter(hasText).join(' ').trim();
  const gr = [name.firstNameGr, name.lastNameGr].filter(hasText).join(' ').trim();
  return (
    <BilingualTextValue en={en || null} gr={gr || null} preferGr={preferGr} emptyLabel={emptyLabel} />
  );
};

const PlainValue: React.FC<{ value?: string | number | null; emptyLabel: string }> = ({
  value,
  emptyLabel,
}) => {
  if (value === null || value === undefined || value === '') {
    return <EmptyHint size="sm">{emptyLabel}</EmptyHint>;
  }
  return (
    <Text size="base" weight="medium" color="text">
      {value}
    </Text>
  );
};

const ProfileField: React.FC<{
  label: string;
  children: React.ReactNode;
  span?: boolean;
}> = ({ label, children, span }) => (
  <InfoItem $span={span}>
    <InfoLabel size="xs" color="secondary">
      {label}
    </InfoLabel>
    {children}
  </InfoItem>
);

/* ─── Component ──────────────────────────────────────────────────────────── */

interface InstitutionProfileProps {
  /** When true, load/save via admin APIs for `/admin/institutions/:id/edit`. */
  adminMode?: boolean;
  /** Dedicated `/profile/edit` entry — start in edit like the member editor. */
  startInEdit?: boolean;
}

const InstitutionProfile: React.FC<InstitutionProfileProps> = ({
  adminMode = false,
  startInEdit = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: routeInstitutionId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const theme = useTheme();
  const { success: showSuccess, error: showError } = useToast();
  const watermark = useMonumentWatermark();
  const preferGr = preferGreek(i18n.language);
  const emptyLabel = t('profile.notProvided');
  const isAdminEdit = adminMode && !!routeInstitutionId;

  const [activeTab, setActiveTab] = useState<string>(
    isAdminEdit || startInEdit ? 'general' : 'overview'
  );
  const [institution, setInstitution] = useState<InstitutionDto | null>(null);
  const [committees, setCommittees] = useState<InternationalCommittee[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(isAdminEdit || startInEdit);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showDeleteDocDialog, setShowDeleteDocDialog] = useState(false);
  const [docToDelete, setDocToDelete] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<DocumentType>(
    DocumentType.FoundationAct
  );
  const [docFile, setDocFile] = useState<File | null>(null);
  const [showAddCommittee, setShowAddCommittee] = useState(false);
  const [selectedCommitteeId, setSelectedCommitteeId] = useState<string>('');
  const [selectedVoteNumber, setSelectedVoteNumber] = useState<string>('');
  const [repFirstNameEn, setRepFirstNameEn] = useState('');
  const [repFirstNameGr, setRepFirstNameGr] = useState('');
  const [repLastNameEn, setRepLastNameEn] = useState('');
  const [repLastNameGr, setRepLastNameGr] = useState('');
  const [repTitle, setRepTitle] = useState('');
  const [repDob, setRepDob] = useState('');
  const [repPositionEn, setRepPositionEn] = useState('');
  const [repPositionGr, setRepPositionGr] = useState('');
  const [repEmail, setRepEmail] = useState('');
  const [repPhone, setRepPhone] = useState('');
  const [addingCommittee, setAddingCommittee] = useState(false);
  const [showRemoveCommitteeDialog, setShowRemoveCommitteeDialog] =
    useState(false);
  const [committeeToRemove, setCommitteeToRemove] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAdminEdit) {
      if (!isAdminUser(user)) {
        navigate('/');
        return;
      }
    } else if (user?.role !== UserRole.InstitutionMember) {
      navigate('/');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, user, isAdminEdit, routeInstitutionId]);

  useEffect(() => {
    if (isAdminEdit) return;
    if (searchParams.get('edit') === '1') {
      const tab = searchParams.get('tab');
      navigate(tab ? `/profile/edit?tab=${tab}` : '/profile/edit', { replace: true });
      return;
    }
    if (startInEdit) {
      const tab = searchParams.get('tab');
      if (tab) setActiveTab(tab);
      setIsEditing(true);
    }
  }, [searchParams, institution, loading, isAdminEdit, startInEdit, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (isAdminEdit && routeInstitutionId) {
        const [institutionData, committeesData] = await Promise.all([
          adminService.getInstitutionById(routeInstitutionId),
          committeeService.getAllCommittees(),
        ]);
        setInstitution(institutionData);
        setCommittees(uniqueInternationalCommittees(committeesData.filter(c => c.isActive)));
        setChangeRequests([]);
        setDocuments([]);
        setIsEditing(true);
        setActiveTab('general');
      } else {
        const [
          institutionData,
          committeesData,
          changeRequestsData,
          documentsData,
        ] = await Promise.all([
          institutionService.getMyInstitution(),
          committeeService.getAllCommittees(),
          changeRequestService.getMyChangeRequests(),
          documentService.getMyDocuments(),
        ]);
        setInstitution(institutionData);
        setCommittees(uniqueInternationalCommittees(committeesData.filter(c => c.isActive)));
        setChangeRequests(changeRequestsData);
        setDocuments(documentsData);
      }
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : t('profile.institutionLoadError')
      );
    } finally {
      setLoading(false);
    }
  };

  const resetAddCommitteeForm = () => {
    setShowAddCommittee(false);
    setSelectedCommitteeId('');
    setSelectedVoteNumber('');
    setRepFirstNameEn('');
    setRepFirstNameGr('');
    setRepLastNameEn('');
    setRepLastNameGr('');
    setRepTitle('');
    setRepDob('');
    setRepPositionEn('');
    setRepPositionGr('');
    setRepEmail('');
    setRepPhone('');
  };

  const hasBilingualName = (name?: BilingualName | null) =>
    !!(
      name?.firstNameEn?.trim() ||
      name?.firstNameGr?.trim() ||
      name?.lastNameEn?.trim() ||
      name?.lastNameGr?.trim()
    );

  const validateInstitutionForm = (): string | null => {
    if (!institution) return t('profile.updateError');
    if (
      !institution.institutionName?.en?.trim() &&
      !institution.institutionName?.gr?.trim()
    ) {
      return t('errors.api.institutionNameRequired');
    }
    if (!hasBilingualName(institution.directorName)) {
      return t('profile.validation.directorNameRequired');
    }
    if (!hasBilingualName(institution.icomRepresentativeName)) {
      return t('profile.validation.icomRepNameRequired');
    }
    const emailFields: Array<[string | undefined, string]> = [
      [institution.institutionEmail, 'institutionEmail'],
      [institution.directorEmail, 'directorEmail'],
      [institution.icomRepresentativeEmail, 'icomRepresentativeEmail'],
      [institution.contactPersonEmail, 'contactPersonEmail'],
    ];
    for (const [value] of emailFields) {
      if (value?.trim()) {
        const result = validation.validateEmail(value);
        if (!result.isValid && result.error) {
          return getValidationErrorMessage(result.error, t);
        }
      }
    }
    const phoneFields = [
      institution.institutionPhone,
      institution.directorPhone,
      institution.icomRepresentativePhone,
      institution.contactPersonPhone,
    ];
    for (const value of phoneFields) {
      if (value?.trim()) {
        const result = validation.validatePhone(value, false);
        if (!result.isValid && result.error) {
          return getValidationErrorMessage(result.error, t);
        }
      }
    }
    if (institution.website?.trim()) {
      const result = validation.validateUrl(institution.website, false);
      if (!result.isValid && result.error) {
        return getValidationErrorMessage(result.error, t);
      }
    }
    return null;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!institution) return;
    setFormError(null);
    const validationError = validateInstitutionForm();
    if (validationError) {
      setFormError(validationError);
      showError(validationError);
      return;
    }
    setSaving(true);
    try {
      if (isAdminEdit && routeInstitutionId) {
        const updated = await adminService.updateInstitution(routeInstitutionId, institution);
        setInstitution(updated);
        showSuccess(t('profile.updateSuccess'));
        setIsEditing(false);
        navigate(`/admin/institutions/${routeInstitutionId}`);
      } else {
        await institutionService.updateMyInstitution(institution);
        showSuccess(t('profile.changeRequestSubmitted'));
        setIsEditing(false);
        if (startInEdit) {
          navigate('/profile');
          return;
        }
        await loadData();
      }
    } catch (error) {
      const raw = error instanceof Error ? error.message : '';
      const msg = getApiErrorMessage(raw, t, { profile: true });
      setFormError(msg);
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormError(null);
    if (isAdminEdit && routeInstitutionId) {
      navigate(`/admin/institutions/${routeInstitutionId}`);
      return;
    }
    if (startInEdit) {
      navigate('/profile');
      return;
    }
    loadData();
    setIsEditing(false);
  };

  const startEditing = (tab?: string) => {
    setFormError(null);
    if (!isAdminEdit && !startInEdit) {
      navigate(tab ? `/profile/edit?tab=${tab}` : '/profile/edit');
      return;
    }
    setIsEditing(true);
    if (tab) setActiveTab(tab);
  };

  const handleAddCommittee = async () => {
    if (!institution || !selectedCommitteeId || !selectedVoteNumber) return;
    const voteNumber = parseInt(selectedVoteNumber, 10);
    if (voteNumber < 1 || voteNumber > 3) {
      showError(t('profile.validation.voteNumberRequired'));
      return;
    }
    if (!repFirstNameEn.trim() && !repFirstNameGr.trim() && !repLastNameEn.trim() && !repLastNameGr.trim()) {
      showError(t('profile.validation.committeeRepNameRequired'));
      return;
    }
    if (!repEmail.trim()) {
      showError(t('profile.validation.committeeRepEmailRequired'));
      return;
    }
    const emailResult = validation.validateEmail(repEmail);
    if (!emailResult.isValid && emailResult.error) {
      showError(getValidationErrorMessage(emailResult.error, t));
      return;
    }
    if (repPhone.trim()) {
      const phoneResult = validation.validatePhone(repPhone, false);
      if (!phoneResult.isValid && phoneResult.error) {
        showError(getValidationErrorMessage(phoneResult.error, t));
        return;
      }
    }
    setAddingCommittee(true);
    try {
      const payload = {
        committeeId: selectedCommitteeId,
        voteNumber,
        name: {
          firstNameEn: repFirstNameEn.trim() || undefined,
          firstNameGr: repFirstNameGr.trim() || undefined,
          lastNameEn: repLastNameEn.trim() || undefined,
          lastNameGr: repLastNameGr.trim() || undefined,
        },
        representativeTitle: repTitle ? parseInt(repTitle, 10) : undefined,
        representativeDateOfBirth: repDob || undefined,
        representativePosition: repPositionEn.trim() || repPositionGr.trim() || undefined,
        representativePositionEn: repPositionEn.trim() || undefined,
        representativePositionGr: repPositionGr.trim() || undefined,
        representativeEmail: repEmail.trim(),
        representativePhone: repPhone.trim() || undefined,
      };
      if (isAdminEdit && routeInstitutionId) {
        await adminService.addInstitutionCommittee(routeInstitutionId, payload);
      } else {
        await institutionService.addCommittee(payload);
      }
      showSuccess(t('profile.committeeAdded'));
      resetAddCommitteeForm();
      await loadData();
    } catch (error) {
      const raw = error instanceof Error ? error.message : '';
      showError(getApiErrorMessage(raw, t) || t('profile.committeeAddError'));
    } finally {
      setAddingCommittee(false);
    }
  };

  const handleRemoveCommittee = async () => {
    if (!committeeToRemove) return;
    try {
      if (isAdminEdit && routeInstitutionId) {
        await adminService.removeInstitutionCommittee(routeInstitutionId, committeeToRemove);
      } else {
        await institutionService.removeCommittee(committeeToRemove);
      }
      showSuccess(t('profile.committeeRemoved'));
      setShowRemoveCommitteeDialog(false);
      setCommitteeToRemove(null);
      await loadData();
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('profile.committeeRemoveError')
      );
    }
  };

  const handleDocumentUpload = async () => {
    if (!docFile || !institution) return;
    setUploadingDoc(true);
    try {
      await documentService.uploadDocument({
        file: docFile,
        documentType: selectedDocType,
        entityType: EntityType.Institution,
        entityId: institution.id,
      });
      showSuccess(t('profile.documentUploaded'));
      setDocFile(null);
      await loadData();
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('profile.documentUploadError')
      );
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocument = async (docId: string) => {
    try {
      const blob = await documentService.downloadDocument(docId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        documents.find(d => d.id === docId)?.originalFileName || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('profile.documentDownloadError')
      );
    }
  };

  const handleDeleteDocument = async () => {
    if (!docToDelete) return;
    try {
      await documentService.deleteDocument(docToDelete);
      showSuccess(t('profile.documentDeleted'));
      setShowDeleteDocDialog(false);
      setDocToDelete(null);
      await loadData();
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('profile.documentDeleteError')
      );
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const updateName = (
    field: 'directorName' | 'icomRepresentativeName',
    patch: Partial<BilingualName>
  ) => {
    if (!institution) return;
    setInstitution({
      ...institution,
      [field]: { ...institution[field], ...patch },
    });
  };

  const updateInstitutionName = (patch: Partial<BilingualText>) => {
    if (!institution) return;
    setInstitution({
      ...institution,
      institutionName: { ...institution.institutionName, ...patch },
    });
  };

  const updateAddress = (
    which: 'primaryAddress' | 'secondaryAddress',
    patch: Partial<BilingualAddress>
  ) => {
    if (!institution) return;
    setInstitution({
      ...institution,
      [which]: { ...institution[which], ...patch },
    });
  };

  const completeness = useMemo(
    () => calculateInstitutionProfileCompletion(institution, t),
    [institution, t]
  );

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!institution) {
    return (
      <InstitutionProfileContainer>
        <Stack direction="column" spacing="lg">
          <PageHeader title={t('profile.institutionProfile')} pattern={true} />
          <Card variant="elevated" accentBorder="left" style={{ padding: spacing[8] }}>
            <Text size="base" color="secondary">
              {t('profile.notFound')}
            </Text>
            <Text size="sm" color="secondary" style={{ marginTop: spacing[4] }}>
              {t('profile.notFoundHint')}
            </Text>
            <Button variant="outline" onClick={() => navigate('/')} style={{ marginTop: spacing[6] }}>
              {t('common.back')}
            </Button>
          </Card>
        </Stack>
      </InstitutionProfileContainer>
    );
  }

  const pendingRequests = changeRequests.filter(
    cr => cr.status === ChangeRequestStatus.Pending
  );
  const currentCommittees = institution.committees || [];
  const availableCommittees = uniqueInternationalCommittees(
    committees,
    selectedCommitteeId ? [selectedCommitteeId] : []
  ).filter(c => {
    if (selectedCommitteeId && c.id === selectedCommitteeId) return true;
    return !currentCommittees.some(
      ic =>
        ic.committeeId === c.id ||
        ic.committee?.id === c.id ||
        (!!c.code && ic.committee?.code?.toLowerCase() === c.code.toLowerCase())
    );
  });
  const occupiedVotes = new Set(
    currentCommittees.map(ic => ic.voteNumber).filter((v): v is number => typeof v === 'number' && v >= 1 && v <= 3)
  );
  const freeVoteOptions = [1, 2, 3]
    .filter(v => !occupiedVotes.has(v))
    .map(v => ({ value: String(v), label: t('profile.voteNumberLabel', { n: v }) }));

  const titleOptions = [
    { value: '', label: t('register.titlePlaceholder') },
    { value: String(Title.Mr), label: t('register.titleMr') },
    { value: String(Title.Ms), label: t('register.titleMs') },
    { value: String(Title.Mrs), label: t('register.titleMrs') },
    { value: String(Title.Dr), label: t('register.titleDr') },
    { value: String(Title.Prof), label: t('register.titleProf') },
    { value: String(Title.Other), label: t('register.titleOther') },
  ];
  const displayName = getInstitutionDisplayName(institution);

  const getAdminStatusLabel = (status?: AdministrativeStatus): string => {
    if (!status) return '';
    const labels: Record<AdministrativeStatus, string> = {
      [AdministrativeStatus.Public]: t('institution.administrativeStatus.public'),
      [AdministrativeStatus.Private]: t('institution.administrativeStatus.private'),
      [AdministrativeStatus.Mixed]: t('institution.administrativeStatus.mixed'),
    };
    return labels[status] || '';
  };

  const getContactPreferenceLabel = (
    pref?: InstitutionContactPreference
  ): string => {
    if (!pref) return '';
    if (pref === InstitutionContactPreference.Primary) {
      return t('register.contactPreferencePrimary');
    }
    if (pref === InstitutionContactPreference.Secondary) {
      return t('register.contactPreferenceSecondary');
    }
    return '';
  };

  const membershipOptions = [
    {
      value: String(InstitutionMembershipCategory.ActiveI),
      label: t('register.budgetLessThan30k'),
    },
    {
      value: String(InstitutionMembershipCategory.ActiveII),
      label: t('register.budget30kTo100k'),
    },
    {
      value: String(InstitutionMembershipCategory.ActiveIII),
      label: t('register.budget100kTo1M'),
    },
    {
      value: String(InstitutionMembershipCategory.ActiveIV),
      label: t('register.budget1MTo5M'),
    },
    {
      value: String(InstitutionMembershipCategory.ActiveV),
      label: t('register.budget5MTo10M'),
    },
    {
      value: String(InstitutionMembershipCategory.ActiveVI),
      label: t('register.budgetMoreThan10M'),
    },
    {
      value: String(InstitutionMembershipCategory.Benefactor),
      label: t('register.budgetBenefactor'),
    },
  ];

  const adminStatusOptions = [
    { value: String(AdministrativeStatus.Public), label: t('institution.administrativeStatus.public') },
    { value: String(AdministrativeStatus.Private), label: t('institution.administrativeStatus.private') },
    { value: String(AdministrativeStatus.Mixed), label: t('institution.administrativeStatus.mixed') },
  ];

  const contactPrefOptions = [
    { value: '', label: t('profile.selectContactPreference') },
    {
      value: String(InstitutionContactPreference.Primary),
      label: t('register.contactPreferencePrimary'),
    },
    {
      value: String(InstitutionContactPreference.Secondary),
      label: t('register.contactPreferenceSecondary'),
    },
  ];

  const documentTypeOptions = [
    { value: DocumentType.FoundationAct.toString(), label: t('profile.docType.foundationAct') },
    { value: DocumentType.IdentityDocument.toString(), label: t('profile.docType.identityDocument') },
    { value: DocumentType.ProofOfAddress.toString(), label: t('profile.docType.proofOfAddress') },
    {
      value: DocumentType.ProfessionalCertificate.toString(),
      label: t('profile.docType.professionalCertificate'),
    },
    { value: DocumentType.Other.toString(), label: t('profile.docType.other') },
  ];

  const getDocumentTypeLabel = (type: DocumentType): string => {
    const match = documentTypeOptions.find(o => o.value === type.toString());
    return match?.label ?? t('profile.docType.other');
  };

  const profileTabs = [
    { id: 'overview', label: t('profile.tabOverview') },
    { id: 'general', label: t('profile.tabGeneral') },
    { id: 'people', label: t('profile.tabPeople') },
    { id: 'committees', label: t('profile.tabCommittees') },
    { id: 'documents', label: t('profile.tabDocuments') },
    { id: 'changes', label: t('profile.tabChangeRequests') },
  ].filter(tab => !isAdminEdit || (tab.id !== 'documents' && tab.id !== 'changes'));

  const renderEditActions = () =>
    isEditing ? (
      <ButtonGroup direction="row" justify="end" spacing="md">
        <Button type="button" variant="outline" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="gradient" glow ripple loading={saving}>
          {t('profile.save')}
        </Button>
      </ButtonGroup>
    ) : null;

  return (
    <InstitutionProfileContainer>
      <Stack direction="column" spacing="xl">
        <HeroCard style={watermark.style}>
          <div style={{ width: '100%', position: 'relative', zIndex: 2 }}>
            <HeroAccentBar aria-hidden />
            <HeroRow direction="row" align="center" gap="lg" style={{ flexWrap: 'wrap' }}>
            <Avatar name={displayName} size="xl" border borderColor="primary" />
            <Stack direction="column" spacing="sm" style={{ flex: 1, minWidth: 0 }}>
              <Heading
                variant="h3"
                color="text"
                style={{
                  fontFamily: typography.fontFamily.display,
                  fontSize: 'clamp(1.75rem, 2.5vw, 2.25rem)',
                  fontWeight: 600,
                  letterSpacing: typography.letterSpacing.tighter,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  margin: 0,
                }}
              >
                {displayName || emptyLabel}
              </Heading>
              {(institution.institutionName?.en &&
                institution.institutionName?.gr &&
                institution.institutionName.en !== institution.institutionName.gr) && (
                <Text size="sm" color="secondary">
                  {preferGr
                    ? institution.institutionName.en
                    : institution.institutionName.gr}
                </Text>
              )}
              <Text size="sm" color="secondary">
                {institution.institutionEmail ||
                  institution.contactPersonEmail ||
                  emptyLabel}
              </Text>
            </Stack>
            {!isEditing ? (
              <Button
                variant="primary"
                onClick={() => startEditing('general')}
              >
                {t('profile.editInstitution')}
              </Button>
            ) : (
              <Button variant="outline" onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
            )}
            </HeroRow>
          </div>
          <MonumentCaption nameEn={watermark.nameEn} nameGr={watermark.nameGr} />
        </HeroCard>

        {pendingRequests.length > 0 && (
          <PendingBadge>
            {t('profile.pendingChangeRequests', { count: pendingRequests.length })}
          </PendingBadge>
        )}

        <Tabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={profileTabs}
          aria-label={t('profile.institutionProfile')}
        >
          {/* ── Overview ───────────────────────────────────────────────── */}
          <TabPanel tabId="overview">
            <OverviewCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
              <Stack direction="column" spacing="lg">
                <div>
                  <Heading variant="h5" color="text">
                    {displayName}
                  </Heading>
                  <Text size="sm" color="secondary" style={{ marginTop: spacing[2] }}>
                    {t('profile.overviewDescription')}
                  </Text>
                </div>

                <CompletenessShell>
                  <Flex direction="row" align="center" justify="space-between" gap="md" style={{ flexWrap: 'wrap' }}>
                    <div>
                      <Text size="base" weight="semibold" color="text">
                        {t('profile.institutionCompletenessTitle')}
                      </Text>
                      <Text size="sm" color="secondary">
                        {t('profile.institutionCompletenessHint')}
                      </Text>
                    </div>
                    <Text size="lg" weight="semibold" color="text">
                      {completeness.percentage}%
                    </Text>
                  </Flex>
                  <CompletenessBarTrack>
                    <CompletenessBarFill $pct={completeness.percentage} />
                  </CompletenessBarTrack>
                  {completeness.missingFieldLabels.length > 0 && (
                    <div>
                      <Text size="sm" weight="medium" color="secondary">
                        {t('profile.institutionMissingFields')}
                      </Text>
                      <MissingList>
                        {completeness.missingFieldLabels.map(label => (
                          <li key={label}>{label}</li>
                        ))}
                      </MissingList>
                    </div>
                  )}
                </CompletenessShell>

                <KeyInfoGrid>
                  <KeyInfoItem>
                    <Text size="xs" color="secondary" style={{ display: 'block', marginBottom: spacing[1] }}>
                      {t('profile.registrationYear')}
                    </Text>
                    <Text size="base" weight="semibold" color="text">
                      {institution.registrationYear}
                    </Text>
                  </KeyInfoItem>
                  <KeyInfoItem>
                    <Text size="xs" color="secondary" style={{ display: 'block', marginBottom: spacing[1] }}>
                      {t('register.membershipCategory')}
                    </Text>
                    <Text size="base" weight="semibold" color="text">
                      {getMembershipCategoryLabel(institution.membershipCategory, t)}
                    </Text>
                  </KeyInfoItem>
                  <KeyInfoItem>
                    <Text size="xs" color="secondary" style={{ display: 'block', marginBottom: spacing[1] }}>
                      {t('register.numberOfMemberCards')}
                    </Text>
                    <Text size="base" weight="semibold" color="text">
                      {institution.numberOfMemberCards}
                    </Text>
                  </KeyInfoItem>
                  <KeyInfoItem>
                    <Text size="xs" color="secondary" style={{ display: 'block', marginBottom: spacing[1] }}>
                      {t('profile.tabCommittees')}
                    </Text>
                    <Text size="base" weight="semibold" color="text">
                      {currentCommittees.length}
                    </Text>
                  </KeyInfoItem>
                  {institution.updatedAt && (
                    <KeyInfoItem>
                      <Text size="xs" color="secondary" style={{ display: 'block', marginBottom: spacing[1] }}>
                        {t('common.updated')}
                      </Text>
                      <Text size="base" weight="semibold" color="text">
                        {formatDate(institution.updatedAt)}
                      </Text>
                    </KeyInfoItem>
                  )}
                </KeyInfoGrid>

                {!isEditing && (
                  <Button
                    variant="gradient"
                    glow
                    ripple
                    onClick={() => startEditing('general')}
                  >
                    {t('profile.editSection')}
                  </Button>
                )}
              </Stack>
            </OverviewCard>
          </TabPanel>

          {/* ── General ────────────────────────────────────────────────── */}
          <TabPanel tabId="general">
            <TabContentCard variant="elevated" accentBorder="top">
              {isEditing ? (
                <form onSubmit={handleSave}>
                  {!isAdminEdit && (
                    <ApprovalBanner>{t('profile.changesRequireApproval')}</ApprovalBanner>
                  )}
                  {isAdminEdit && (
                    <ApprovalBanner>{t('profile.adminDirectSaveHint', {
                      defaultValue: 'As an administrator, changes are saved immediately to this institution profile.',
                    })}</ApprovalBanner>
                  )}
                  {formError && <FormAlert variant="error">{formError}</FormAlert>}
                  <Stack direction="column" spacing="lg">
                    <FormSection
                      title={t('profile.institutionSectionBasics')}
                      description={t('profile.institutionSectionBasicsHint')}
                    >
                      <FormGrid>
                        <FormField
                          label={t('register.institutionNameEn')}
                          name="institutionNameEn"
                          value={institution.institutionName?.en ?? ''}
                          onChange={e => updateInstitutionName({ en: e.target.value })}
                          required
                        />
                        <FormField
                          label={t('register.institutionNameGr')}
                          name="institutionNameGr"
                          value={institution.institutionName?.gr ?? ''}
                          onChange={e => updateInstitutionName({ gr: e.target.value })}
                        />
                        <FormField
                          label={t('register.website')}
                          name="website"
                          type="url"
                          value={institution.website || ''}
                          onChange={e =>
                            setInstitution({ ...institution, website: e.target.value })
                          }
                        />
                        <FormField
                          label={t('register.foundationDate')}
                          name="foundationDate"
                          type="date"
                          value={
                            institution.foundationDate
                              ? institution.foundationDate.split('T')[0]
                              : ''
                          }
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              foundationDate: e.target.value,
                            })
                          }
                        />
                        <FormField
                          label={t('register.administrativeStatus')}
                          name="administrativeStatus"
                          select
                          options={[
                            { value: '', label: t('common.select') },
                            ...adminStatusOptions,
                          ]}
                          value={
                            institution.administrativeStatus != null
                              ? String(institution.administrativeStatus)
                              : ''
                          }
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              administrativeStatus: e.target.value
                                ? (parseInt(e.target.value, 10) as AdministrativeStatus)
                                : undefined,
                            })
                          }
                        />
                        <FormField
                          label={t('institution.icomType.label')}
                          name="institutionType"
                          select
                          helperText={t('institution.icomType.intro')}
                          options={[
                            { value: '', label: t('institution.icomType.required') },
                            ...ICOM_INSTITUTION_TYPE_KEYS.map(key => ({
                              value: key,
                              label: t(`institution.icomType.options.${key}`),
                            })),
                          ]}
                          value={institution.institutionType?.[0] ?? ''}
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              institutionType: e.target.value ? [e.target.value] : [],
                              collectionTypes: undefined,
                            })
                          }
                        />
                      </FormGrid>
                    </FormSection>

                    <FormSection
                      title={t('profile.institutionSectionAddress')}
                      description={t('profile.institutionSectionAddressHint')}
                    >
                      <FormGrid>
                        <FormField
                          label={`${t('register.institutionAddress')} (${t('register.languageShortEn')})`}
                          name="addressEn"
                          value={institution.primaryAddress?.addressEn ?? ''}
                          onChange={e => updateAddress('primaryAddress', { addressEn: e.target.value })}
                        />
                        <FormField
                          label={`${t('register.institutionAddress')} (${t('register.languageShortGr')})`}
                          name="addressGr"
                          value={institution.primaryAddress?.addressGr ?? ''}
                          onChange={e => updateAddress('primaryAddress', { addressGr: e.target.value })}
                        />
                        <FormField
                          label={`${t('register.institutionCity')} (${t('register.languageShortEn')})`}
                          name="cityEn"
                          value={institution.primaryAddress?.cityEn ?? ''}
                          onChange={e => updateAddress('primaryAddress', { cityEn: e.target.value })}
                        />
                        <FormField
                          label={`${t('register.institutionCity')} (${t('register.languageShortGr')})`}
                          name="cityGr"
                          value={institution.primaryAddress?.cityGr ?? ''}
                          onChange={e => updateAddress('primaryAddress', { cityGr: e.target.value })}
                        />
                        <FormField
                          label={`${t('register.institutionPostalCode')} (${t('register.languageShortEn')})`}
                          name="postalCodeEn"
                          value={institution.primaryAddress?.postalCodeEn ?? ''}
                          onChange={e =>
                            updateAddress('primaryAddress', { postalCodeEn: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.institutionPostalCode')} (${t('register.languageShortGr')})`}
                          name="postalCodeGr"
                          value={institution.primaryAddress?.postalCodeGr ?? ''}
                          onChange={e =>
                            updateAddress('primaryAddress', { postalCodeGr: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.country')} (${t('register.languageShortEn')})`}
                          name="countryEn"
                          value={institution.primaryAddress?.countryEn ?? ''}
                          onChange={e => updateAddress('primaryAddress', { countryEn: e.target.value })}
                        />
                        <FormField
                          label={`${t('register.country')} (${t('register.languageShortGr')})`}
                          name="countryGr"
                          value={institution.primaryAddress?.countryGr ?? ''}
                          onChange={e => updateAddress('primaryAddress', { countryGr: e.target.value })}
                        />
                        <PhoneWithDialCode
                          id="profile-institution-phone"
                          name="institutionPhone"
                          label={t('register.individual.telephoneWithCountryCode')}
                          value={institution.institutionPhone || ''}
                          onChange={phone =>
                            setInstitution({
                              ...institution,
                              institutionPhone: phone,
                            })
                          }
                        />
                        <FormField
                          label={t('register.institutionEmail')}
                          name="institutionEmail"
                          type="email"
                          value={institution.institutionEmail || ''}
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              institutionEmail: e.target.value,
                            })
                          }
                        />
                      </FormGrid>
                    </FormSection>

                    <FormSection
                      title={t('profile.institutionSectionSecondaryAddress')}
                      description={t('profile.institutionSectionSecondaryAddressHint')}
                    >
                      <FormGrid>
                        <FormField
                          label={`${t('register.secondaryAddress')} (${t('register.languageShortEn')})`}
                          name="secondaryAddressEn"
                          value={institution.secondaryAddress?.addressEn ?? ''}
                          onChange={e =>
                            updateAddress('secondaryAddress', { addressEn: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.secondaryAddress')} (${t('register.languageShortGr')})`}
                          name="secondaryAddressGr"
                          value={institution.secondaryAddress?.addressGr ?? ''}
                          onChange={e =>
                            updateAddress('secondaryAddress', { addressGr: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.institutionCity')} (${t('register.languageShortEn')})`}
                          name="secondaryCityEn"
                          value={institution.secondaryAddress?.cityEn ?? ''}
                          onChange={e =>
                            updateAddress('secondaryAddress', { cityEn: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.institutionCity')} (${t('register.languageShortGr')})`}
                          name="secondaryCityGr"
                          value={institution.secondaryAddress?.cityGr ?? ''}
                          onChange={e =>
                            updateAddress('secondaryAddress', { cityGr: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.institutionPostalCode')} (${t('register.languageShortEn')})`}
                          name="secondaryPostalEn"
                          value={institution.secondaryAddress?.postalCodeEn ?? ''}
                          onChange={e =>
                            updateAddress('secondaryAddress', { postalCodeEn: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.institutionPostalCode')} (${t('register.languageShortGr')})`}
                          name="secondaryPostalGr"
                          value={institution.secondaryAddress?.postalCodeGr ?? ''}
                          onChange={e =>
                            updateAddress('secondaryAddress', { postalCodeGr: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.country')} (${t('register.languageShortEn')})`}
                          name="secondaryCountryEn"
                          value={institution.secondaryAddress?.countryEn ?? ''}
                          onChange={e =>
                            updateAddress('secondaryAddress', { countryEn: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.country')} (${t('register.languageShortGr')})`}
                          name="secondaryCountryGr"
                          value={institution.secondaryAddress?.countryGr ?? ''}
                          onChange={e =>
                            updateAddress('secondaryAddress', { countryGr: e.target.value })
                          }
                        />
                        <FormField
                          label={t('register.contactPreference')}
                          name="icomContactPreference"
                          select
                          options={contactPrefOptions}
                          value={
                            institution.icomContactPreference != null
                              ? String(institution.icomContactPreference)
                              : ''
                          }
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              icomContactPreference: e.target.value
                                ? (parseInt(e.target.value, 10) as InstitutionContactPreference)
                                : undefined,
                            })
                          }
                        />
                      </FormGrid>
                    </FormSection>

                    <FormSection
                      title={t('profile.institutionSectionMembership')}
                      description={t('profile.institutionSectionMembershipHint')}
                    >
                      <FormGrid>
                        <FormField
                          label={t('register.membershipCategory')}
                          name="membershipCategory"
                          select
                          options={[
                            ...(tryNormalizeEnumValue(
                              InstitutionMembershipCategory,
                              institution.membershipCategory
                            ) == null
                              ? [
                                  {
                                    value: '',
                                    label: t('common.unknown', { defaultValue: 'Unknown' }),
                                  },
                                ]
                              : []),
                            ...membershipOptions,
                          ]}
                          value={
                            tryNormalizeEnumValue(
                              InstitutionMembershipCategory,
                              institution.membershipCategory
                            ) != null
                              ? String(
                                  tryNormalizeEnumValue(
                                    InstitutionMembershipCategory,
                                    institution.membershipCategory
                                  )
                                )
                              : ''
                          }
                          onChange={e => {
                            const parsed = tryNormalizeEnumValue(
                              InstitutionMembershipCategory,
                              e.target.value
                            );
                            if (parsed == null) return;
                            setInstitution({
                              ...institution,
                              membershipCategory: parsed,
                            });
                          }}
                        />
                        <FormField
                          label={t('register.numberOfMemberCards')}
                          name="numberOfMemberCards"
                          value={String(institution.numberOfMemberCards)}
                          disabled
                          helperText={t('profile.memberCardsAutoHint')}
                        />
                        <FormField
                          label={t('profile.registrationYear')}
                          name="registrationYear"
                          value={String(institution.registrationYear)}
                          disabled
                        />
                      </FormGrid>
                    </FormSection>

                    {renderEditActions()}
                  </Stack>
                </form>
              ) : (
                <Stack direction="column" spacing="none">
                  <SectionBlock>
                    <SectionTitle>{t('profile.institutionSectionBasics')}</SectionTitle>
                    <SectionHint size="sm" color="secondary">
                      {t('profile.institutionSectionBasicsHint')}
                    </SectionHint>
                    <InfoGrid>
                      <ProfileField label={t('register.institutionName')} span>
                        <BilingualTextValue
                          en={institution.institutionName?.en}
                          gr={institution.institutionName?.gr}
                          preferGr={preferGr}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.website')}>
                        {institution.website ? (
                          <Text size="base" weight="medium" color="text">
                            <a
                              href={institution.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: theme.colors.primary }}
                            >
                              {institution.website}
                            </a>
                          </Text>
                        ) : (
                          <EmptyHint size="sm">{emptyLabel}</EmptyHint>
                        )}
                      </ProfileField>
                      <ProfileField label={t('register.foundationDate')}>
                        <PlainValue
                          value={
                            institution.foundationDate
                              ? formatDate(institution.foundationDate)
                              : null
                          }
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.administrativeStatus')}>
                        <PlainValue
                          value={getAdminStatusLabel(institution.administrativeStatus) || null}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('profile.institutionType')}>
                        <PlainValue
                          value={
                            institution.institutionType?.length
                              ? institution.institutionType
                                  .map(v => formatInstitutionTypeLabel(v, t))
                                  .join(', ')
                              : null
                          }
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      {!!institution.collectionTypes?.length && (
                      <ProfileField label={t('profile.collectionTypes')}>
                        <PlainValue
                          value={institution.collectionTypes.join(', ')}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      )}
                    </InfoGrid>
                  </SectionBlock>

                  <SectionBlock>
                    <SectionTitle>{t('profile.institutionSectionAddress')}</SectionTitle>
                    <SectionHint size="sm" color="secondary">
                      {t('profile.institutionSectionAddressHint')}
                    </SectionHint>
                    <InfoGrid>
                      <ProfileField label={t('register.institutionAddress')} span>
                        <BilingualTextValue
                          en={institution.primaryAddress?.addressEn}
                          gr={institution.primaryAddress?.addressGr}
                          preferGr={preferGr}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.institutionCity')}>
                        <BilingualTextValue
                          en={institution.primaryAddress?.cityEn}
                          gr={institution.primaryAddress?.cityGr}
                          preferGr={preferGr}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.institutionPostalCode')}>
                        <BilingualTextValue
                          en={institution.primaryAddress?.postalCodeEn}
                          gr={institution.primaryAddress?.postalCodeGr}
                          preferGr={preferGr}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.country')}>
                        <BilingualTextValue
                          en={institution.primaryAddress?.countryEn}
                          gr={institution.primaryAddress?.countryGr}
                          preferGr={preferGr}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.institutionTel')}>
                        <PlainValue
                          value={institution.institutionPhone}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.institutionEmail')}>
                        <PlainValue
                          value={institution.institutionEmail}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                    </InfoGrid>
                  </SectionBlock>

                  <SectionBlock>
                    <SectionTitle>
                      {t('profile.institutionSectionSecondaryAddress')}
                    </SectionTitle>
                    <InfoGrid>
                      <ProfileField label={t('register.secondaryAddress')} span>
                        <BilingualTextValue
                          en={institution.secondaryAddress?.addressEn}
                          gr={institution.secondaryAddress?.addressGr}
                          preferGr={preferGr}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.institutionCity')}>
                        <BilingualTextValue
                          en={institution.secondaryAddress?.cityEn}
                          gr={institution.secondaryAddress?.cityGr}
                          preferGr={preferGr}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.contactPreference')}>
                        <PlainValue
                          value={
                            getContactPreferenceLabel(institution.icomContactPreference) ||
                            null
                          }
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                    </InfoGrid>
                  </SectionBlock>

                  <SectionBlock>
                    <SectionTitle>{t('profile.institutionSectionMembership')}</SectionTitle>
                    <InfoGrid>
                      <ProfileField label={t('register.membershipCategory')}>
                        <PlainValue
                          value={getMembershipCategoryLabel(
                            institution.membershipCategory,
                            t
                          )}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.numberOfMemberCards')}>
                        <PlainValue
                          value={institution.numberOfMemberCards}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('profile.registrationYear')}>
                        <PlainValue
                          value={institution.registrationYear}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                    </InfoGrid>
                  </SectionBlock>

                  <Button variant="outline" onClick={() => startEditing('general')}>
                    {t('profile.editSection')}
                  </Button>
                </Stack>
              )}
            </TabContentCard>
          </TabPanel>

          {/* ── People ─────────────────────────────────────────────────── */}
          <TabPanel tabId="people">
            <TabContentCard variant="elevated" accentBorder="top">
              {isEditing ? (
                <form onSubmit={handleSave}>
                  {!isAdminEdit && (
                    <ApprovalBanner>{t('profile.changesRequireApproval')}</ApprovalBanner>
                  )}
                  {isAdminEdit && (
                    <ApprovalBanner>
                      {t('profile.adminDirectSaveHint', {
                        defaultValue:
                          'As an administrator, changes are saved immediately to this institution profile.',
                      })}
                    </ApprovalBanner>
                  )}
                  {formError && <FormAlert variant="error">{formError}</FormAlert>}
                  <Stack direction="column" spacing="lg">
                    <FormSection
                      title={t('profile.institutionSectionDirector')}
                      description={t('profile.institutionSectionDirectorHint')}
                    >
                      <FormGrid>
                        <FormField
                          label={t('register.titleOption')}
                          name="directorTitle"
                          select
                          options={titleOptions}
                          value={
                            institution.directorTitle != null
                              ? String(institution.directorTitle)
                              : ''
                          }
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              directorTitle: e.target.value
                                ? parseInt(e.target.value, 10)
                                : undefined,
                            })
                          }
                        />
                        <FormField
                          label={t('profile.dateOfBirth')}
                          name="directorDateOfBirth"
                          type="date"
                          value={
                            institution.directorDateOfBirth
                              ? institution.directorDateOfBirth.split('T')[0]
                              : ''
                          }
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              directorDateOfBirth: e.target.value || undefined,
                            })
                          }
                        />
                        <FormField
                          label={`${t('register.directorFirstName')} (${t('register.languageShortEn')})`}
                          name="directorFirstNameEn"
                          value={institution.directorName?.firstNameEn ?? ''}
                          onChange={e =>
                            updateName('directorName', { firstNameEn: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.directorFirstName')} (${t('register.languageShortGr')})`}
                          name="directorFirstNameGr"
                          value={institution.directorName?.firstNameGr ?? ''}
                          onChange={e =>
                            updateName('directorName', { firstNameGr: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.directorLastName')} (${t('register.languageShortEn')})`}
                          name="directorLastNameEn"
                          value={institution.directorName?.lastNameEn ?? ''}
                          onChange={e =>
                            updateName('directorName', { lastNameEn: e.target.value })
                          }
                        />
                        <FormField
                          label={`${t('register.directorLastName')} (${t('register.languageShortGr')})`}
                          name="directorLastNameGr"
                          value={institution.directorName?.lastNameGr ?? ''}
                          onChange={e =>
                            updateName('directorName', { lastNameGr: e.target.value })
                          }
                        />
                        <FormField
                          label={t('register.directorEmail')}
                          name="directorEmail"
                          type="email"
                          value={institution.directorEmail || ''}
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              directorEmail: e.target.value,
                            })
                          }
                        />
                        <PhoneWithDialCode
                          id="profile-director-phone"
                          name="directorPhone"
                          label={t('register.individual.telephoneWithCountryCode')}
                          value={institution.directorPhone || ''}
                          onChange={phone =>
                            setInstitution({
                              ...institution,
                              directorPhone: phone,
                            })
                          }
                        />
                      </FormGrid>
                    </FormSection>

                    <FormSection
                      title={t('profile.institutionSectionIcomRep')}
                      description={t('profile.institutionSectionIcomRepHint')}
                    >
                      <FormGrid>
                        <FormField
                          label={t('register.titleOption')}
                          name="icomRepresentativeTitle"
                          select
                          options={titleOptions}
                          value={
                            institution.icomRepresentativeTitle != null
                              ? String(institution.icomRepresentativeTitle)
                              : ''
                          }
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              icomRepresentativeTitle: e.target.value
                                ? parseInt(e.target.value, 10)
                                : undefined,
                            })
                          }
                        />
                        <FormField
                          label={t('profile.dateOfBirth')}
                          name="icomRepresentativeDateOfBirth"
                          type="date"
                          value={
                            institution.icomRepresentativeDateOfBirth
                              ? institution.icomRepresentativeDateOfBirth.split('T')[0]
                              : ''
                          }
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              icomRepresentativeDateOfBirth: e.target.value || undefined,
                            })
                          }
                        />
                        <FormField
                          label={`${t('register.icomRepresentativeFirstName')} (${t('register.languageShortEn')})`}
                          name="icomRepFirstNameEn"
                          value={institution.icomRepresentativeName?.firstNameEn ?? ''}
                          onChange={e =>
                            updateName('icomRepresentativeName', {
                              firstNameEn: e.target.value,
                            })
                          }
                        />
                        <FormField
                          label={`${t('register.icomRepresentativeFirstName')} (${t('register.languageShortGr')})`}
                          name="icomRepFirstNameGr"
                          value={institution.icomRepresentativeName?.firstNameGr ?? ''}
                          onChange={e =>
                            updateName('icomRepresentativeName', {
                              firstNameGr: e.target.value,
                            })
                          }
                        />
                        <FormField
                          label={`${t('register.icomRepresentativeLastName')} (${t('register.languageShortEn')})`}
                          name="icomRepLastNameEn"
                          value={institution.icomRepresentativeName?.lastNameEn ?? ''}
                          onChange={e =>
                            updateName('icomRepresentativeName', {
                              lastNameEn: e.target.value,
                            })
                          }
                        />
                        <FormField
                          label={`${t('register.icomRepresentativeLastName')} (${t('register.languageShortGr')})`}
                          name="icomRepLastNameGr"
                          value={institution.icomRepresentativeName?.lastNameGr ?? ''}
                          onChange={e =>
                            updateName('icomRepresentativeName', {
                              lastNameGr: e.target.value,
                            })
                          }
                        />
                        <FormField
                          label={t('register.icomRepresentativePosition')}
                          name="icomRepresentativePosition"
                          value={institution.icomRepresentativePosition || ''}
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              icomRepresentativePosition: e.target.value,
                            })
                          }
                        />
                        <FormField
                          label={t('register.icomRepresentativeEmail')}
                          name="icomRepresentativeEmail"
                          type="email"
                          value={institution.icomRepresentativeEmail || ''}
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              icomRepresentativeEmail: e.target.value,
                            })
                          }
                        />
                        <PhoneWithDialCode
                          id="profile-icom-rep-phone"
                          name="icomRepresentativePhone"
                          label={t('register.individual.telephoneWithCountryCode')}
                          value={institution.icomRepresentativePhone || ''}
                          onChange={phone =>
                            setInstitution({
                              ...institution,
                              icomRepresentativePhone: phone,
                            })
                          }
                        />
                        <FormField
                          label={t('register.languageForCommunication')}
                          name="icomRepresentativeCommunicationLanguage"
                          select
                          options={[
                            { value: '', label: t('common.select') },
                            { value: CommunicationLanguage.English, label: t('register.languageEnglish') },
                            { value: CommunicationLanguage.Greek, label: t('register.languageGreek') },
                            { value: CommunicationLanguage.French, label: t('register.languageFrench') },
                          ]}
                          value={
                            normalizeCommunicationLanguage(
                              institution.icomRepresentativeCommunicationLanguage
                            ) ?? ''
                          }
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              icomRepresentativeCommunicationLanguage: e.target.value
                                ? (e.target.value as CommunicationLanguage)
                                : undefined,
                            })
                          }
                        />
                      </FormGrid>
                    </FormSection>

                    <FormSection
                      title={t('profile.institutionSectionContactPerson')}
                      description={t('profile.institutionSectionContactPersonHint')}
                    >
                      <FormGrid>
                        <FormField
                          label={t('register.contactPersonFirstName')}
                          name="contactPersonFirstName"
                          value={institution.contactPersonFirstName || ''}
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              contactPersonFirstName: e.target.value,
                            })
                          }
                        />
                        <FormField
                          label={t('register.contactPersonLastName')}
                          name="contactPersonLastName"
                          value={institution.contactPersonLastName || ''}
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              contactPersonLastName: e.target.value,
                            })
                          }
                        />
                        <FormField
                          label={t('register.contactPersonEmail')}
                          name="contactPersonEmail"
                          type="email"
                          value={institution.contactPersonEmail || ''}
                          onChange={e =>
                            setInstitution({
                              ...institution,
                              contactPersonEmail: e.target.value,
                            })
                          }
                        />
                        <PhoneWithDialCode
                          id="profile-contact-person-phone"
                          name="contactPersonPhone"
                          label={t('register.individual.telephoneWithCountryCode')}
                          value={institution.contactPersonPhone || ''}
                          onChange={phone =>
                            setInstitution({
                              ...institution,
                              contactPersonPhone: phone,
                            })
                          }
                        />
                      </FormGrid>
                    </FormSection>

                    {renderEditActions()}
                  </Stack>
                </form>
              ) : (
                <Stack direction="column" spacing="none">
                  <SectionBlock>
                    <SectionTitle>{t('profile.institutionSectionDirector')}</SectionTitle>
                    <InfoGrid>
                      <ProfileField label={t('register.titleOption')}>
                        <PlainValue
                          value={
                            titleOptions.find(
                              o => o.value === String(institution.directorTitle)
                            )?.label || null
                          }
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('profile.dateOfBirth')}>
                        <PlainValue
                          value={
                            institution.directorDateOfBirth
                              ? formatDate(institution.directorDateOfBirth)
                              : null
                          }
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.institutionDirector')} span>
                        <BilingualNameValue
                          name={institution.directorName}
                          preferGr={preferGr}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.directorEmail')}>
                        <PlainValue
                          value={institution.directorEmail}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.directorPhone')}>
                        <PlainValue
                          value={institution.directorPhone}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                    </InfoGrid>
                  </SectionBlock>

                  <SectionBlock>
                    <SectionTitle>{t('profile.institutionSectionIcomRep')}</SectionTitle>
                    <InfoGrid>
                      <ProfileField label={t('register.titleOption')}>
                        <PlainValue
                          value={
                            titleOptions.find(
                              o => o.value === String(institution.icomRepresentativeTitle)
                            )?.label || null
                          }
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('profile.dateOfBirth')}>
                        <PlainValue
                          value={
                            institution.icomRepresentativeDateOfBirth
                              ? formatDate(institution.icomRepresentativeDateOfBirth)
                              : null
                          }
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.icomRepresentative')} span>
                        <BilingualNameValue
                          name={institution.icomRepresentativeName}
                          preferGr={preferGr}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.icomRepresentativePosition')}>
                        <PlainValue
                          value={institution.icomRepresentativePosition}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.icomRepresentativeEmail')}>
                        <PlainValue
                          value={institution.icomRepresentativeEmail}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.icomRepresentativePhone')}>
                        <PlainValue
                          value={institution.icomRepresentativePhone}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.languageForCommunication')}>
                        <PlainValue
                          value={
                            (() => {
                              const lang = normalizeCommunicationLanguage(
                                institution.icomRepresentativeCommunicationLanguage
                              );
                              if (lang === CommunicationLanguage.English) return t('register.languageEnglish');
                              if (lang === CommunicationLanguage.Greek) return t('register.languageGreek');
                              if (lang === CommunicationLanguage.French) return t('register.languageFrench');
                              return null;
                            })()
                          }
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                    </InfoGrid>
                  </SectionBlock>

                  <SectionBlock>
                    <SectionTitle>
                      {t('profile.institutionSectionContactPerson')}
                    </SectionTitle>
                    <InfoGrid>
                      <ProfileField label={t('register.contactPerson')}>
                        <PlainValue
                          value={
                            [institution.contactPersonFirstName, institution.contactPersonLastName]
                              .filter(Boolean)
                              .join(' ')
                              .trim() || null
                          }
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.contactPersonEmail')}>
                        <PlainValue
                          value={institution.contactPersonEmail}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                      <ProfileField label={t('register.contactPersonPhone')}>
                        <PlainValue
                          value={institution.contactPersonPhone}
                          emptyLabel={emptyLabel}
                        />
                      </ProfileField>
                    </InfoGrid>
                  </SectionBlock>

                  <Button variant="outline" onClick={() => startEditing('people')}>
                    {t('profile.editSection')}
                  </Button>
                </Stack>
              )}
            </TabContentCard>
          </TabPanel>

          {/* ── Committees ─────────────────────────────────────────────── */}
          <TabPanel tabId="committees">
            <TabContentCard variant="elevated" accentBorder="top">
              <FormSection
                title={t('profile.committees')}
                description={t('profile.institutionCommitteesHint')}
              >
                {currentCommittees.length === 0 ? (
                  <EmptyState>{t('profile.noCommitteesYet')}</EmptyState>
                ) : (
                  <CommitteeList>
                    {currentCommittees.map(ic => {
                      const repName = [
                        preferGr
                          ? ic.name?.firstNameGr || ic.name?.firstNameEn
                          : ic.name?.firstNameEn || ic.name?.firstNameGr,
                        preferGr
                          ? ic.name?.lastNameGr || ic.name?.lastNameEn
                          : ic.name?.lastNameEn || ic.name?.lastNameGr,
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                      <CommitteeItem key={ic.id}>
                        <div>
                          <Text size="sm" weight="medium" color="text">
                            {preferGr
                              ? ic.committee?.nameGr || ic.committee?.nameEn
                              : ic.committee?.nameEn || ic.committee?.nameGr}
                          </Text>
                          <Text size="xs" color="secondary">
                            {ic.committee?.code}
                            {ic.voteNumber != null && (
                              <> · {t('profile.voteNumberLabel', { n: ic.voteNumber })}</>
                            )}
                            {repName && <> · {repName}</>}
                            {(preferGr
                              ? ic.representativePositionGr || ic.representativePositionEn || ic.representativePosition
                              : ic.representativePositionEn || ic.representativePositionGr || ic.representativePosition) && (
                              <>
                                {' '}
                                ·{' '}
                                {preferGr
                                  ? ic.representativePositionGr || ic.representativePositionEn || ic.representativePosition
                                  : ic.representativePositionEn || ic.representativePositionGr || ic.representativePosition}
                              </>
                            )}
                            {ic.representativeEmail && <> · {ic.representativeEmail}</>}
                          </Text>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCommitteeToRemove(ic.id);
                            setShowRemoveCommitteeDialog(true);
                          }}
                          style={{
                            borderColor: theme.colors.error,
                            color: theme.colors.error,
                            minWidth: 'auto',
                          }}
                        >
                          {t('common.remove')}
                        </Button>
                      </CommitteeItem>
                      );
                    })}
                  </CommitteeList>
                )}
                {currentCommittees.length < 3 && freeVoteOptions.length > 0 && (
                  <AddCommitteeSection>
                    {!showAddCommittee ? (
                      <Button type="button" onClick={() => setShowAddCommittee(true)}>
                        {t('profile.addCommittee')}
                      </Button>
                    ) : (
                      <>
                        <FormGrid>
                          <FormField
                            label={t('profile.selectCommittee')}
                            select
                            options={[
                              { value: '', label: t('profile.selectCommitteePlaceholder') },
                              ...availableCommittees.map(c => ({
                                value: c.id,
                                label: `${preferGr ? c.nameGr || c.nameEn : c.nameEn || c.nameGr} (${c.code})`,
                              })),
                            ]}
                            value={selectedCommitteeId}
                            onChange={e => setSelectedCommitteeId(e.target.value)}
                          />
                          <FormField
                            label={t('profile.voteNumber')}
                            select
                            options={[
                              { value: '', label: t('profile.selectVoteNumber') },
                              ...freeVoteOptions,
                            ]}
                            value={selectedVoteNumber}
                            onChange={e => setSelectedVoteNumber(e.target.value)}
                            helperText={t('profile.voteNumberHint')}
                          />
                          <FormField
                            label={t('register.titleOption')}
                            select
                            options={titleOptions}
                            value={repTitle}
                            onChange={e => setRepTitle(e.target.value)}
                          />
                          <FormField
                            label={t('profile.dateOfBirth')}
                            type="date"
                            value={repDob}
                            onChange={e => setRepDob(e.target.value)}
                          />
                          <FormField
                            label={`${t('register.firstName')} (${t('register.languageShortEn')})`}
                            value={repFirstNameEn}
                            onChange={e => setRepFirstNameEn(e.target.value)}
                          />
                          <FormField
                            label={`${t('register.firstName')} (${t('register.languageShortGr')})`}
                            value={repFirstNameGr}
                            onChange={e => setRepFirstNameGr(e.target.value)}
                          />
                          <FormField
                            label={`${t('register.lastName')} (${t('register.languageShortEn')})`}
                            value={repLastNameEn}
                            onChange={e => setRepLastNameEn(e.target.value)}
                          />
                          <FormField
                            label={`${t('register.lastName')} (${t('register.languageShortGr')})`}
                            value={repLastNameGr}
                            onChange={e => setRepLastNameGr(e.target.value)}
                          />
                          <FormField
                            label={`${t('register.icomRepresentativePosition')} (${t('register.languageShortEn')})`}
                            value={repPositionEn}
                            onChange={e => setRepPositionEn(e.target.value)}
                          />
                          <FormField
                            label={`${t('register.icomRepresentativePosition')} (${t('register.languageShortGr')})`}
                            value={repPositionGr}
                            onChange={e => setRepPositionGr(e.target.value)}
                          />
                          <FormField
                            label={t('common.email')}
                            type="email"
                            value={repEmail}
                            onChange={e => setRepEmail(e.target.value)}
                            required
                          />
                          <PhoneWithDialCode
                            id="profile-committee-rep-phone"
                            label={t('register.individual.telephoneWithCountryCode')}
                            value={repPhone}
                            onChange={setRepPhone}
                          />
                        </FormGrid>
                        <div
                          style={{
                            display: 'flex',
                            gap: spacing[2],
                            marginTop: spacing[4],
                          }}
                        >
                          <Button
                            type="button"
                            onClick={handleAddCommittee}
                            disabled={
                              addingCommittee ||
                              !selectedCommitteeId ||
                              !selectedVoteNumber
                            }
                          >
                            {addingCommittee ? t('common.saving') : t('common.add')}
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={resetAddCommitteeForm}
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </>
                    )}
                  </AddCommitteeSection>
                )}
              </FormSection>
            </TabContentCard>
          </TabPanel>

          {/* ── Documents ──────────────────────────────────────────────── */}
          <TabPanel tabId="documents">
            <TabContentCard variant="elevated" accentBorder="top">
              <FormSection
                title={t('profile.documents')}
                description={t('profile.institutionDocumentsHint')}
              >
                <Stack direction="column" spacing="lg">
                  <div>
                    <FormField
                      label={t('profile.documentType')}
                      select
                      options={documentTypeOptions}
                      value={selectedDocType.toString()}
                      onChange={e =>
                        setSelectedDocType(parseInt(e.target.value, 10) as DocumentType)
                      }
                      disabled={uploadingDoc}
                    />
                    <FileUpload
                      onFileSelect={setDocFile}
                      onFileRemove={() => setDocFile(null)}
                      currentFile={docFile}
                      accept=".pdf,.jpg,.jpeg,.png"
                      maxSize={5 * 1024 * 1024}
                      label={t('profile.uploadDocument')}
                      hint={t('profile.uploadDocumentHint')}
                    />
                    {docFile && (
                      <Button
                        type="button"
                        onClick={handleDocumentUpload}
                        disabled={uploadingDoc}
                        style={{ marginTop: spacing[3] }}
                      >
                        {uploadingDoc
                          ? t('profile.uploading')
                          : t('profile.uploadDocument')}
                      </Button>
                    )}
                  </div>
                  {documents.length === 0 ? (
                    <EmptyState>{t('profile.noDocumentsYet')}</EmptyState>
                  ) : (
                    documents.map(doc => (
                      <DocumentItem key={doc.id}>
                        <DocumentInfo>
                          <DocumentName>{doc.originalFileName}</DocumentName>
                          <DocumentMeta>
                            {getDocumentTypeLabel(doc.documentType)}
                            {' · '}
                            {formatFileSize(doc.fileSize)}
                            {' · '}
                            {formatDate(doc.uploadedAt)}
                            {doc.isVerified && (
                              <span style={{ marginLeft: spacing[2] }}>
                                <StatusIndicator
                                  status="success"
                                  size="sm"
                                  label={t('profile.verified')}
                                />
                              </span>
                            )}
                          </DocumentMeta>
                        </DocumentInfo>
                        <DocumentActions>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadDocument(doc.id)}
                          >
                            {t('common.download')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setDocToDelete(doc.id);
                              setShowDeleteDocDialog(true);
                            }}
                            style={{
                              borderColor: theme.colors.error,
                              color: theme.colors.error,
                              minWidth: 'auto',
                            }}
                          >
                            {t('common.delete')}
                          </Button>
                        </DocumentActions>
                      </DocumentItem>
                    ))
                  )}
                </Stack>
              </FormSection>
            </TabContentCard>
          </TabPanel>

          {/* ── Change requests ────────────────────────────────────────── */}
          <TabPanel tabId="changes">
            <TabContentCard variant="elevated" accentBorder="top">
              <FormSection title={t('profile.pendingRequests')}>
                {pendingRequests.length === 0 ? (
                  <EmptyState>{t('profile.noPendingChanges')}</EmptyState>
                ) : (
                  <Stack direction="column" spacing="md">
                    {pendingRequests.map(cr => (
                      <ChangeRequestCard key={cr.id}>
                        <ChangeRequestRow>
                          <Text size="sm" color="secondary">
                            {t('profile.changeField')}
                          </Text>
                          <Text size="sm" weight="semibold" color="text">
                            {getInstitutionFieldLabel(cr.fieldName, t)}
                          </Text>
                        </ChangeRequestRow>
                        <ChangeRequestRow>
                          <Text size="sm" color="secondary">
                            {t('common.status')}
                          </Text>
                          <StatusIndicator
                            status={
                              cr.status === ChangeRequestStatus.Approved
                                ? 'success'
                                : cr.status === ChangeRequestStatus.Rejected
                                  ? 'error'
                                  : 'warning'
                            }
                            size="sm"
                            label={
                              cr.status === ChangeRequestStatus.Approved
                                ? t('grouping.status.approved')
                                : cr.status === ChangeRequestStatus.Rejected
                                  ? t('grouping.status.rejected')
                                  : t('grouping.status.pending')
                            }
                          />
                        </ChangeRequestRow>
                        <ChangeRequestRow>
                          <Text size="sm" color="secondary">
                            {t('profile.requestedAt')}
                          </Text>
                          <Text size="sm" weight="semibold" color="text">
                            {formatDate(cr.requestedAt)}
                          </Text>
                        </ChangeRequestRow>
                      </ChangeRequestCard>
                    ))}
                  </Stack>
                )}
              </FormSection>
            </TabContentCard>
          </TabPanel>
        </Tabs>

        <ConfirmDialog
          isOpen={showDeleteDocDialog}
          onClose={() => {
            setShowDeleteDocDialog(false);
            setDocToDelete(null);
          }}
          onConfirm={handleDeleteDocument}
          title={t('profile.deleteDocumentTitle')}
          message={t('profile.deleteDocumentMessage')}
          confirmText={t('common.delete')}
          cancelText={t('common.cancel')}
          variant="danger"
        />
        <ConfirmDialog
          isOpen={showRemoveCommitteeDialog}
          onClose={() => {
            setShowRemoveCommitteeDialog(false);
            setCommitteeToRemove(null);
          }}
          onConfirm={handleRemoveCommittee}
          title={t('profile.removeCommitteeTitle')}
          message={t('profile.removeCommitteeMessage')}
          confirmText={t('common.remove')}
          cancelText={t('common.cancel')}
          variant="danger"
        />
      </Stack>
    </InstitutionProfileContainer>
  );
};

export default InstitutionProfile;
